import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import './Chat.css';

const MessageInput = ({ onSendMessage, disabled }) => {
  const [message, setMessage] = useState('');
  const [cannedResponses, setCannedResponses] = useState([]);
  const [showCannedResponses, setShowCannedResponses] = useState(false);
  const [filteredResponses, setFilteredResponses] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef(null);

  // Fetch canned responses on mount
  useEffect(() => {
    fetchCannedResponses();
  }, []);

  const fetchCannedResponses = async () => {
    try {
      const response = await api.get('/v1/conversations/canned-responses/?is_active=true');
      const items = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.results)
        ? response.data.results
        : [];
      setCannedResponses(items);
    } catch (error) {
      console.error('Error fetching canned responses:', error);
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setMessage(value);

    // Check if user is typing a canned response trigger
    const lastWord = value.split(/\s/).pop();
    if (lastWord.startsWith('/') && lastWord.length > 1) {
      const query = lastWord.substring(1).toLowerCase();
      const filtered = cannedResponses.filter((response) =>
        response.shortcut.toLowerCase().includes(query)
      );
      setFilteredResponses(filtered);
      setShowCannedResponses(filtered.length > 0);
      setSelectedIndex(0);
    } else {
      setShowCannedResponses(false);
    }
  };

  const selectCannedResponse = async (response) => {
    // Replace the trigger text with the canned response
    const words = message.split(/\s/);
    words.pop(); // Remove the trigger
    const newMessage = words.length > 0 ? words.join(' ') + ' ' + response.message : response.message;
    setMessage(newMessage);
    setShowCannedResponses(false);

    // Track usage
    try {
      await api.post(`/v1/conversations/canned-responses/${response.id}/use/`);
    } catch (error) {
      console.error('Error tracking canned response usage:', error);
    }

    // Focus back on textarea
    textareaRef.current?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      await onSendMessage(message);
      setMessage('');
    }
  };

  const handleKeyPress = (e) => {
    // Handle autocomplete navigation
    if (showCannedResponses) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredResponses.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredResponses.length) % filteredResponses.length);
      } else if (e.key === 'Tab' || e.key === 'Enter') {
        if (filteredResponses.length > 0) {
          e.preventDefault();
          selectCannedResponse(filteredResponses[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        setShowCannedResponses(false);
      }
      return;
    }

    // Send on Enter, new line on Shift+Enter
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="message-input-wrapper">
      {showCannedResponses && (
        <div className="canned-responses-dropdown">
          {filteredResponses.map((response, index) => (
            <div
              key={response.id}
              className={`canned-response-item ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => selectCannedResponse(response)}
            >
              <div className="canned-response-shortcut">/{response.shortcut}</div>
              <div className="canned-response-message">{response.message}</div>
            </div>
          ))}
        </div>
      )}
      <form className="message-input-container" onSubmit={handleSubmit}>
        <textarea
          ref={textareaRef}
          className="message-input"
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyPress}
          placeholder="Type a message... (Use / for canned responses)"
          disabled={disabled}
          rows="1"
        />
        <button
          type="submit"
          className="send-button"
          disabled={!message.trim() || disabled}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </form>
    </div>
  );
};

export default MessageInput;
